import type JSONAPISerializer from '@ember-data/serializer/json-api';

import ApplicationSerializer from './application';

/** Write-only options passed to `invite.save({ adapterOptions })`; injected into
 *  the create payload by `serializeIntoHash`, never round-tripped on reads. */
/** A draft pre-reg document to attach on invite create. `templateId` rides in the
 *  relationship item's `meta` because the backend convert step pairs the converted
 *  document to its `CustomUserDocumentTemplate` by id — the draft resource itself
 *  does not carry the template id back. */
export interface InviteDraftDocument {
  id: string;
  templateId: string;
}

export interface InviteSaveAdapterOptions {
  workflowReevaluationReason?: string | null;
  draftDocuments?: InviteDraftDocument[];
}

export default class InviteSerializer extends ApplicationSerializer {
  attrs = {
    anonymizedAt: {
      serialize: false,
    },
    createdAt: {
      serialize: false,
    },
    creator: {
      serialize: false,
    },
    visitorDocuments: {
      serialize: false,
    },
    locationNames: {
      serialize: false,
    },
    visitApproval: {
      serialize: false,
    },
  };

  normalize(...args: Parameters<JSONAPISerializer['normalize']>): ReturnType<JSONAPISerializer['normalize']> {
    const hash = <{ relationships: { flow?: { data?: { id: string } } } }>args[1];

    // flow relationship comes back as an empty id for workplace. Remove that so ember data doesn't error.
    if (hash.relationships && !hash.relationships['flow']?.data?.id) delete hash.relationships['flow'];
    return super.normalize(...args);
  }

  serializeIntoHash(...args: Parameters<ApplicationSerializer['serializeIntoHash']>): void {
    const [hash, , snapshot, options] = args;
    const payload = this.serialize(snapshot, options!) as Record<string, unknown>;
    const adapterOptions = snapshot.adapterOptions as InviteSaveAdapterOptions | undefined;

    if (adapterOptions && 'workflowReevaluationReason' in adapterOptions) {
      const reason = adapterOptions.workflowReevaluationReason;
      const trimmed = typeof reason === 'string' ? reason.trim() : '';
      const meta = (payload['meta'] ?? {}) as Record<string, unknown>;
      meta['reason'] = trimmed ? trimmed : null;
      payload['meta'] = meta;
    }

    // Write-only `draft-documents` relationship for admin pre-reg uploads: sent
    // only on create (via adapterOptions) and never present on invite reads, so
    // it can't be a normal Ember Data relationship. Each item carries its
    // `template-id` in `meta` — the backend convert step pairs the converted
    // document to its template by that id.
    const draftDocuments = adapterOptions?.draftDocuments;
    // Create-only: enforce at the serializer (not just by caller convention) since
    // serializeIntoHash also runs on PATCH. A new (unsaved) invite has no id yet, so this
    // gates injection to create — a future update-caller passing draftDocuments must not
    // silently attach auto-approved drafts to an already-persisted invite.
    if (draftDocuments?.length && !snapshot.id) {
      const data = (payload['data'] ?? {}) as { relationships?: Record<string, unknown> };
      data.relationships = data.relationships ?? {};
      data.relationships['draft-documents'] = {
        data: draftDocuments.map(({ id, templateId }) => ({
          type: 'draft-documents',
          id,
          meta: { 'template-id': templateId },
        })),
      };
      payload['data'] = data;
    }

    Object.assign(hash, payload);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/serializer' {
  export default interface SerializerRegistry {
    invite: InviteSerializer;
  }
}
